import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LocalizedLink from './LocalizedLink';
import { Hash, Copy, Check } from 'lucide-react';
import SEOHead from './SEOHead';

const NumbersConverterIndex = () => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('42');
  const [inputBase, setInputBase] = useState('10');
  const [customBase, setCustomBase] = useState('');
  const [error, setError] = useState('');
  const [conversions, setConversions] = useState({});
  const [copiedBase, setCopiedBase] = useState(null);

  // Base definitions
  const commonBases = [
    { id: '2', name: t('converters.numbers.binary'), label: t('converters.numbers.binaryLabel') },
    { id: '8', name: t('converters.numbers.octal'), label: t('converters.numbers.octalLabel') },
    { id: '10', name: t('converters.numbers.decimal'), label: t('converters.numbers.decimalLabel') },
    { id: '16', name: t('converters.numbers.hexadecimal'), label: t('converters.numbers.hexadecimalLabel') },
    { id: 'custom', name: 'Custom', label: t('converters.numbers.customLabel') },
  ];

  // Validate input based on the selected base
  const validateInput = (value, base) => {
    if (!value || value.trim() === '') {
      return { valid: false, error: t('converters.numbers.pleaseEnterValue') };
    }

    const baseNum = parseInt(base);
    if (isNaN(baseNum) || baseNum < 2 || baseNum > 36) {
      return { valid: false, error: t('converters.numbers.baseMustBeBetween') };
    }

    // Remove whitespace and convert to uppercase for validation
    const cleanValue = value.trim().toUpperCase();

    // Define valid characters for the base
    const validChars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'.slice(0, baseNum);

    // Check if all characters are valid for this base
    for (let char of cleanValue) {
      if (!validChars.includes(char)) {
        return {
          valid: false,
          error: t('converters.numbers.invalidCharacter', { char, base: baseNum, validChars })
        };
      }
    }

    return { valid: true, error: '' };
  };

  // Convert number from input base to decimal
  const toDecimal = (value, fromBase) => {
    const cleanValue = value.trim().toUpperCase();
    return parseInt(cleanValue, parseInt(fromBase));
  };

  // Convert decimal to target base
  const fromDecimal = (decimalValue, toBase) => {
    if (isNaN(decimalValue)) return '';
    const baseNum = parseInt(toBase);
    return decimalValue.toString(baseNum).toUpperCase();
  };

  // Perform all conversions
  useEffect(() => {
    const currentBase = inputBase === 'custom' ? customBase : inputBase;

    if (!currentBase) {
      setError(t('converters.numbers.pleaseSelectBase'));
      setConversions({});
      return;
    }

    const validation = validateInput(inputValue, currentBase);

    if (!validation.valid) {
      setError(validation.error);
      setConversions({});
      return;
    }

    setError('');

    try {
      // Convert input to decimal first
      const decimalValue = toDecimal(inputValue, currentBase);

      if (isNaN(decimalValue)) {
        setError(t('converters.numbers.invalidNumberFormat'));
        setConversions({});
        return;
      }

      // Convert to all common bases
      const newConversions = {
        binary: fromDecimal(decimalValue, 2),
        octal: fromDecimal(decimalValue, 8),
        decimal: fromDecimal(decimalValue, 10),
        hexadecimal: fromDecimal(decimalValue, 16),
      };

      setConversions(newConversions);
    } catch (err) {
      setError(t('converters.numbers.errorConverting') + ': ' + err.message);
      setConversions({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, inputBase, customBase, t]);

  // Copy to clipboard
  const copyToClipboard = async (text, baseName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedBase(baseName);
      setTimeout(() => setCopiedBase(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Handle base change
  const handleBaseChange = (e) => {
    setInputBase(e.target.value);
    if (e.target.value !== 'custom') {
      setCustomBase('');
    }
  };

  // Get current base for display
  const getCurrentBase = () => {
    return inputBase === 'custom' ? customBase : inputBase;
  };

  return (
    <div className="space-y-6">
      <SEOHead
        title={t('converters.numbers.name')}
        description={t('converters.numbers.seoDescription')}
      />

      {/* Header with Description */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#4a7c9b] to-[#5a8caa] px-4 py-3">
          <div className="flex items-center gap-2">
            <Hash className="h-5 w-5 text-white" />
            <h1 className="text-lg font-semibold text-white">{t('converters.numbers.title', t('converters.numbers.name'))}</h1>
          </div>
        </div>
        <div className="p-4">
          <p className="text-gray-600 text-sm">
            {t('converters.numbers.pageDescription')}
          </p>
        </div>
      </div>

      {/* Main Converter Tool */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6">
          <div className="space-y-4">
            {/* Input Section */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">{t('converters.numbers.enterNumber')}</label>
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4a7c9b] focus:border-transparent text-lg font-mono"
                placeholder={t('converters.numbers.enterNumber')}
              />
            </div>

            {/* Base Selection */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">{t('converters.numbers.inputBase')}</label>
                <select
                  value={inputBase}
                  onChange={handleBaseChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#4a7c9b] focus:border-transparent"
                >
                  {commonBases.map((base) => (
                    <option key={base.id} value={base.id}>
                      {base.label}
                    </option>
                  ))}
                </select>
              </div>

              {inputBase === 'custom' && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">{t('converters.numbers.customBase')}</label>
                  <input
                    type="number"
                    min="2"
                    max="36"
                    value={customBase}
                    onChange={(e) => setCustomBase(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4a7c9b] focus:border-transparent text-lg"
                    placeholder={t('converters.numbers.enterBase')}
                  />
                </div>
              )}
            </div>

            {/* Error Display */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Input Value Display */}
            {!error && inputValue && getCurrentBase() && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-center text-lg text-gray-700">
                  <span className="font-semibold font-mono">{inputValue.toUpperCase()}</span>{' '}
                  <span className="text-gray-500">{t('converters.numbers.inBase', { base: getCurrentBase() })}</span>
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Conversion Results */}
        {!error && Object.keys(conversions).length > 0 && (
          <div className="border-t border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">{t('converters.numbers.conversions')}</h3>
            <div className="space-y-3">
              {/* Binary */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex-1">
                  <div className="text-xs font-medium text-gray-500 mb-1">{t('converters.numbers.binaryLabel')}</div>
                  <div className="text-lg font-mono text-gray-800 break-all">{conversions.binary}</div>
                </div>
                <button
                  onClick={() => copyToClipboard(conversions.binary, 'binary')}
                  className="ml-4 p-2 rounded-lg bg-white border border-gray-300 hover:bg-gray-100 transition-colors"
                  title={t('converters.numbers.copyToClipboard')}
                >
                  {copiedBase === 'binary' ? (
                    <Check className="h-5 w-5 text-green-600" />
                  ) : (
                    <Copy className="h-5 w-5 text-gray-600" />
                  )}
                </button>
              </div>

              {/* Octal */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex-1">
                  <div className="text-xs font-medium text-gray-500 mb-1">{t('converters.numbers.octalLabel')}</div>
                  <div className="text-lg font-mono text-gray-800 break-all">{conversions.octal}</div>
                </div>
                <button
                  onClick={() => copyToClipboard(conversions.octal, 'octal')}
                  className="ml-4 p-2 rounded-lg bg-white border border-gray-300 hover:bg-gray-100 transition-colors"
                  title={t('converters.numbers.copyToClipboard')}
                >
                  {copiedBase === 'octal' ? (
                    <Check className="h-5 w-5 text-green-600" />
                  ) : (
                    <Copy className="h-5 w-5 text-gray-600" />
                  )}
                </button>
              </div>

              {/* Decimal */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex-1">
                  <div className="text-xs font-medium text-gray-500 mb-1">{t('converters.numbers.decimalLabel')}</div>
                  <div className="text-lg font-mono text-gray-800 break-all">{conversions.decimal}</div>
                </div>
                <button
                  onClick={() => copyToClipboard(conversions.decimal, 'decimal')}
                  className="ml-4 p-2 rounded-lg bg-white border border-gray-300 hover:bg-gray-100 transition-colors"
                  title={t('converters.numbers.copyToClipboard')}
                >
                  {copiedBase === 'decimal' ? (
                    <Check className="h-5 w-5 text-green-600" />
                  ) : (
                    <Copy className="h-5 w-5 text-gray-600" />
                  )}
                </button>
              </div>

              {/* Hexadecimal */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex-1">
                  <div className="text-xs font-medium text-gray-500 mb-1">{t('converters.numbers.hexadecimalLabel')}</div>
                  <div className="text-lg font-mono text-gray-800 break-all">{conversions.hexadecimal}</div>
                </div>
                <button
                  onClick={() => copyToClipboard(conversions.hexadecimal, 'hexadecimal')}
                  className="ml-4 p-2 rounded-lg bg-white border border-gray-300 hover:bg-gray-100 transition-colors"
                  title={t('converters.numbers.copyToClipboard')}
                >
                  {copiedBase === 'hexadecimal' ? (
                    <Check className="h-5 w-5 text-green-600" />
                  ) : (
                    <Copy className="h-5 w-5 text-gray-600" />
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Common Number Base Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-800">{t('converters.numbers.commonNumberBases')}</h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-800 mb-2">{t('converters.numbers.binaryLabel')}</h3>
              <p className="text-sm text-gray-600 mb-2">
                {t('converters.numbers.binaryDescription')}
              </p>
              <p className="text-xs text-gray-500 font-mono">{t('converters.numbers.example')} 1010 = 10 in decimal</p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-800 mb-2">{t('converters.numbers.octalLabel')}</h3>
              <p className="text-sm text-gray-600 mb-2">
                {t('converters.numbers.octalDescription')}
              </p>
              <p className="text-xs text-gray-500 font-mono">{t('converters.numbers.example')} 12 = 10 in decimal</p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-800 mb-2">{t('converters.numbers.decimalLabel')}</h3>
              <p className="text-sm text-gray-600 mb-2">
                {t('converters.numbers.decimalDescription')}
              </p>
              <p className="text-xs text-gray-500 font-mono">{t('converters.numbers.example')} 10 = 10 in decimal</p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-800 mb-2">{t('converters.numbers.hexadecimalLabel')}</h3>
              <p className="text-sm text-gray-600 mb-2">
                {t('converters.numbers.hexadecimalDescription')}
              </p>
              <p className="text-xs text-gray-500 font-mono">{t('converters.numbers.example')} A = 10 in decimal</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Examples */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-800">{t('converters.numbers.quickExamples')}</h2>
        </div>
        <div className="p-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left font-medium text-gray-600">{t('converters.numbers.decimal')}</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">{t('converters.numbers.binary')}</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">{t('converters.numbers.octal')}</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">{t('converters.numbers.hexadecimal')}</th>
                </tr>
              </thead>
              <tbody>
                {[0, 1, 8, 10, 15, 16, 32, 64, 100, 255].map((num) => (
                  <tr key={num} className="border-t border-gray-100">
                    <td className="px-3 py-2 font-mono">{num}</td>
                    <td className="px-3 py-2 font-mono text-[#4a7c9b]">{num.toString(2)}</td>
                    <td className="px-3 py-2 font-mono text-[#4a7c9b]">{num.toString(8)}</td>
                    <td className="px-3 py-2 font-mono text-[#4a7c9b]">{num.toString(16).toUpperCase()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Back to Home Link */}
      <div className="text-center">
        <LocalizedLink
          to="/"
          className="text-[#2563eb] hover:underline text-sm"
        >
          ← {t('common.backToAllConverters')}
        </LocalizedLink>
      </div>
    </div>
  );
};

export default NumbersConverterIndex;
